/* eslint-disable no-lonely-if */
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FlagsSelect from '@orientaction/components/CountryFlagSelector/FlagsSelect';
import MaterialPhoneInput from '@orientaction/components/MaterialPhoneInput';
import { SMS_ALLOWED_COUNTRIES } from '@orientaction/components/MaterialPhoneInput/smsAllowedCountries';
import {
  TURNSTILE_TOKEN_FIELD,
  TurnstileWidget,
  useTurnstile,
} from '@orientaction/components/Turnstile';
import { useOfflineLanguage, useResponseUser } from '@orientaction/hooks';
import { userService } from '@orientaction/services';
import { emailMatch, isAdmin, publicIconPath } from '@orientaction/utils';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { FormEvent, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useHistory, useLocation } from 'react-router-dom';
import config from '../../../../services/config';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { getAppleConnectUrl } from '../../../../utils/sso';
import RedesignImage from '../../shared/components/RedesignImage';
import { useAuthStyles } from '../Auth.styles';
import HelpModal from '../components/HelpModal';
import locale from './locale.json';
import { useSignupFormStyles } from './SignupForm.styles';

const PASSWORD_PATTERN =
  /^(((?=.*[!@#$%^&*])(?=.*[A-Z])(?=.*[a-z]))|((?=.*\d)(?=.*[A-Z])(?=.*[a-z]))|((?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z]))|((?=.*\d)(?=.*[!@#$%^&*])(?=.*[A-Z]))).{8,}$/;

const SignupForm = () => {
  const shared = useAuthStyles();
  const local = useSignupFormStyles();
  const cls = { ...shared, ...local };
  const history = useHistory();
  const { search } = useLocation();
  const isSso = useMemo(() => new URLSearchParams(search).get('sso') === 'true', [search]);
  const idGameResult = new URLSearchParams(search).get('idResult');
  const idGame = new URLSearchParams(search).get('idGame');

  const [step, setStep] = useState(isSso ? 1 : 0);
  const [step1Data, setStep1Data] = useState<Record<string, any>>({});
  const [step2Data, setStep2Data] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [eyeOn, setEyeOn] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [step2Errors, setStep2Errors] = useState<Record<string, string>>({});
  const [helpOpen, setHelpOpen] = useState(false);
  const turnstile = useTurnstile();

  const { language, onLanguageChange } = useOfflineLanguage();
  const { postResponseUserConnected, responses } = useResponseUser();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ mode: 'onChange', defaultValues: step1Data });

  const newPasswordRef = useRef('');
  newPasswordRef.current = watch('password', '');
  const confirmPasswordRef = useRef('');
  confirmPasswordRef.current = watch('confirmPassword', '');

  const loginLink = !idGameResult ? '/' : `/?from=test&idResult=${idGameResult}&idGame=${idGame}`;

  const onStep1Submit = async (data: Record<string, any>) => {
    const turnstileToken = turnstile.getTokenOrError();
    if (!turnstileToken) {
      return;
    }

    setIsLoading(true);
    setRegisterError('');
    try {
      await userService.verifyIfMailExist(data.email, turnstileToken);
      turnstile.reset();
      setStep1Data(data);
      setStep(1);
    } catch {
      turnstile.reset();
      setRegisterError(
        "Cette adresse e-mail est déjà utilisée. Veuillez vous connecter ou changer d'adresse pour créer votre compte"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const validateStep2 = () => {
    const errorForm: Record<string, string> = {};

    if (!step2Data.phone || step2Data.phone.length <= 2) {
      errorForm.phone = 'Champ obligatoire';
    } else if (!isValidPhoneNumber(step2Data.phone)) {
      errorForm.phone = 'Le format du numéro est invalide';
    }

    if (!step2Data.postal_code) {
      errorForm.postal_code = 'Champ obligatoire';
    }

    setStep2Errors(errorForm);
    return Object.keys(errorForm).length > 0;
  };

  const onStep2Submit = async (e: FormEvent) => {
    e.preventDefault();
    if (validateStep2()) return;

    let turnstileToken: string | null = null;
    if (!isSso) {
      turnstileToken = turnstile.getTokenOrError();
      if (!turnstileToken) {
        return;
      }
    }

    setIsLoading(true);
    setRegisterError('');

    try {
      if (isSso) {
        const raw = sessionStorage.getItem('ssoData');
        if (!raw) {
          setRegisterError('Données SSO introuvables. Veuillez vous reconnecter.');
          return;
        }
        const { jwt, user } = JSON.parse(raw);
        await userService.modifyUser({
          id: user.id,
          phone: step2Data.phone,
          postal_code: step2Data.postal_code,
          token: jwt,
        });

        sessionStorage.removeItem('ssoData');
        localStorage.setItem('userToken', jwt);
        localStorage.setItem(
          'userInfos',
          JSON.stringify({ ...user, phone: step2Data.phone, postal_code: step2Data.postal_code })
        );

        if (isAdmin(user)) {
          const services = user?.allowed_services?.map((s: { id: number }) => s.id) ?? [];
          if (services.length === 1 && services[0] === 4) {
            history.replace('/admin/emploi/board');
            return;
          }
          history.replace('/admin/users-resultats');
          return;
        }
        history.replace('/main');
        return;
      }

      const from = new URLSearchParams(search).get('from');
      const registerData = await userService.registerUser({
        ...step1Data,
        ...step2Data,
        language: language === 'GB' ? 2 : 1,
        fromTest: from === 'test',
        company: 1,
        username: step1Data.email,
        [TURNSTILE_TOKEN_FIELD]: turnstileToken,
      });

      if (registerData?.data?.jwt) {
        localStorage.setItem('userInfos', JSON.stringify(registerData?.data?.user));
        localStorage.setItem('userToken', registerData?.data?.jwt);

        if (responses) {
          const dataToPost = {
            ...responses,
            users_permissions_users: [registerData?.data?.user.id],
          };
          const datareturn = await postResponseUserConnected(dataToPost, registerData?.data?.jwt);
          history.push(`/congrats/${datareturn.id}`);
        } else if (idGameResult) {
          await apiServiceNative.put(`/api/update-result-game/${idGameResult}`, {
            user_id: registerData?.data?.user.id,
            forBrevo: true,
          });
          history.push(`/game/play/${idGameResult}/0`);
        } else if (registerData?.data?.user?.role?.id === 3) {
          history.push('/results');
        } else {
          history.push('/main');
        }
      } else if (registerData?.data?.user?.confirmed === false) {
        history.push({
          pathname: '/signup/confirmation',
          state: { email: step1Data.email, phone: step2Data.phone },
        });
      }
    } catch {
      turnstile.reset();
      setRegisterError('Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 1) {
    return (
      <>
        <div className={cls.headingGroup}>
          <h1 className={cls.title}>{locale.step2Title}</h1>
          <p className={cls.subtitle}>{locale.step2Subtitle}</p>
        </div>

        <form onSubmit={onStep2Submit} className={cls.form}>
          <div className={cls.fieldsGroup}>
            <label htmlFor="signup-phone" className={cls.field}>
              <span className={cls.label}>{locale.phoneLabel}</span>
              <MaterialPhoneInput
                id="signup-phone"
                value={step2Data.phone}
                onChange={(value: any) => setStep2Data(prev => ({ ...prev, phone: value }))}
                error={!!step2Errors.phone}
                className={cls.materialPhoneInput}
                allowedCountries={SMS_ALLOWED_COUNTRIES}
              />
              {step2Errors.phone && <span className={cls.errorText}>{step2Errors.phone}</span>}
            </label>

            <label htmlFor="signup-postal-code" className={cls.field}>
              <span className={cls.label}>{locale.postalCodeLabel}</span>
              <input
                id="signup-postal-code"
                name="postal_code"
                placeholder={locale.postalCodePlaceholder}
                value={step2Data.postal_code || ''}
                onChange={e => {
                  const { value } = e.target;
                  if (value !== '' && !/^\d+$/.test(value)) return;
                  setStep2Data(prev => ({ ...prev, postal_code: value }));
                }}
                inputMode="numeric"
                className={cls.input}
              />
              {step2Errors.postal_code && (
                <span className={cls.errorText}>{step2Errors.postal_code}</span>
              )}
            </label>
          </div>

          {registerError && <p className={cls.errorTextMd}>{registerError}</p>}

          {!isSso && <TurnstileWidget {...turnstile.widgetProps} error={turnstile.error} />}

          <Button
            type="submit"
            className={cls.primaryButton}
            disabled={isLoading}
            data-cy="save-user">
            {isLoading ? `${locale.registrationText}...` : locale.registrationText}
          </Button>

          {!isSso && (
            <button
              type="button"
              className={cls.backButton}
              onClick={() => {
                turnstile.reset();
                setStep(0);
              }}>
              <RedesignImage
                src={`${publicIconPath}/ArrowLeft.svg`}
                alt=""
                width={16}
                height={16}
              />
              <span>{locale.backText}</span>
            </button>
          )}
        </form>
      </>
    );
  }

  return (
    <>
      <div className={cls.headingGroup}>
        <h1 className={cls.title}>{locale.step1Title}</h1>
      </div>

      <Box className={cls.languageSelector}>
        <FlagsSelect
          countries={['FR', 'GB']}
          selected={language}
          onSelect={onLanguageChange}
          classes={{ button: cls.languageSelectorButton }}
        />
      </Box>

      <form onSubmit={handleSubmit(onStep1Submit)} className={cls.form}>
        <div className={cls.fieldsGroup}>
          <Controller
            name="lastname"
            control={control}
            rules={{ required: 'Champ obligatoire' }}
            render={({ field, fieldState }) => (
              <label htmlFor="signup-lastname" className={cls.field}>
                <span className={cls.label}>{`${locale.lastname} *`}</span>
                <input
                  {...field}
                  id="signup-lastname"
                  type="text"
                  placeholder={locale.lastnamePlaceholder}
                  className={cls.input}
                />
                {fieldState.error?.message && (
                  <span className={cls.errorText}>{fieldState.error.message}</span>
                )}
              </label>
            )}
          />

          <Controller
            name="firstname"
            control={control}
            rules={{ required: 'Champ obligatoire' }}
            render={({ field, fieldState }) => (
              <label htmlFor="signup-firstname" className={cls.field}>
                <span className={cls.label}>{`${locale.firstname} *`}</span>
                <input
                  {...field}
                  id="signup-firstname"
                  type="text"
                  placeholder={locale.firstnamePlaceholder}
                  className={cls.input}
                />
                {fieldState.error?.message && (
                  <span className={cls.errorText}>{fieldState.error.message}</span>
                )}
              </label>
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={{
              required: 'Champ obligatoire',
              pattern: {
                value: emailMatch,
                message: 'Veuillez saisir une adresse e-mail valide',
              },
            }}
            render={({ field, fieldState }) => (
              <label htmlFor="signup-email" className={cls.field}>
                <span className={cls.label}>{`${locale.email} *`}</span>
                <div className={cls.inputWrapper}>
                  <input
                    {...field}
                    id="signup-email"
                    type="text"
                    placeholder={locale.emailPlaceholder}
                    className={`${cls.input} ${cls.inputWithIcon}`}
                  />
                  <RedesignImage
                    src={`${publicIconPath}/Email.svg`}
                    alt=""
                    className={cls.inputIcon}
                    style={{ cursor: 'default' }}
                  />
                </div>
                <p className={cls.helperText}>{locale.emailHelperText}</p>
                {fieldState.error?.message && (
                  <span className={cls.errorText}>{fieldState.error.message}</span>
                )}
              </label>
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={{
              required: 'Champ obligatoire',
              pattern: {
                value: PASSWORD_PATTERN,
                message:
                  'Votre mot de passe doit contenir au moins 8 caractères et au moins 3 des éléments suivants :\n- Lettres minuscules (a-z)\n- Lettres majuscules (A-Z)\n- Chiffres (0-9)\n- Caractères spéciaux (ex. !@#)}',
              },
            }}
            render={({ field, fieldState }) => (
              <label htmlFor="signup-password" className={cls.field}>
                <span className={cls.label}>{`${locale.password} *`}</span>
                <div className={cls.inputWrapper}>
                  <input
                    {...field}
                    id="signup-password"
                    type={eyeOn ? 'text' : 'password'}
                    placeholder={locale.passwordPlaceholder}
                    className={`${cls.input} ${cls.inputWithIcon}`}
                  />
                  <button
                    type="button"
                    className={cls.inputIcon}
                    onClick={() => setEyeOn(prev => !prev)}
                    aria-label={eyeOn ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}>
                    <RedesignImage
                      src={`${publicIconPath}/${eyeOn ? 'EyeOpened' : 'EyeClosed'}.svg`}
                      alt=""
                    />
                  </button>
                </div>
                {fieldState.error?.message && (
                  <span className={`${cls.errorText} ${cls.errorTextMultiline}`}>
                    {fieldState.error.message}
                  </span>
                )}
              </label>
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: 'Champ obligatoire',
              validate: value =>
                value === newPasswordRef.current || "Le mot de passe n'est pas identique",
            }}
            render={({ field, fieldState }) => (
              <label htmlFor="signup-confirm-password" className={cls.field}>
                <span className={cls.label}>{`${locale.confirmPassword} *`}</span>
                <div className={cls.inputWrapper}>
                  <input
                    {...field}
                    id="signup-confirm-password"
                    type={eyeOn ? 'text' : 'password'}
                    placeholder={locale.confirmPasswordPlaceholder}
                    className={`${cls.input} ${cls.inputWithIcon}`}
                  />
                  <button
                    type="button"
                    className={cls.inputIcon}
                    onClick={() => setEyeOn(prev => !prev)}
                    aria-label={eyeOn ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}>
                    <RedesignImage
                      src={`${publicIconPath}/${eyeOn ? 'EyeOpened' : 'EyeClosed'}.svg`}
                      alt=""
                    />
                  </button>
                </div>
                {fieldState.error?.message && (
                  <span className={cls.errorText}>{fieldState.error.message}</span>
                )}
              </label>
            )}
          />
        </div>

        <span className={cls.helperText}>{locale.requiredFieldsText}</span>

        <div className={cls.checkboxRow}>
          <Controller
            name="cgu"
            control={control}
            rules={{ required: "Veuillez accepter les conditions générales d'utilisation" }}
            render={({ field }) => (
              <Checkbox
                {...field}
                checked={!!field.value}
                className={cls.checkbox}
                id="cgu"
                color="primary"
                data-cy="cgu-cy"
              />
            )}
          />
          <label htmlFor="cgu">
            <p className={cls.checkboxLabel}>
              {locale.cguAcceptText}{' '}
              <Link
                className={cls.linkPrimary}
                to="../../../../../orientaction-cgu.pdf"
                target="_blank">
                {locale.cguLinkText}
              </Link>
              <span> et la </span>
              <Link
                className={cls.linkPrimary}
                to="../../../../../orientaction-pdc.pdf"
                target="_blank">
                {locale.privacyPolicyText}
              </Link>
            </p>
          </label>
        </div>

        {(errors as any).cgu && (
          <span className={cls.errorText}>{(errors as any).cgu.message}</span>
        )}

        {registerError && <p className={cls.errorTextMd}>{registerError}</p>}

        <TurnstileWidget {...turnstile.widgetProps} error={turnstile.error} />

        <Button
          type="submit"
          className={cls.primaryButton}
          disabled={isLoading}
          data-cy="step-one-register">
          {isLoading ? 'Suivant...' : 'Suivant'}
        </Button>
      </form>

      <div className={cls.divider}>
        <div className={cls.dividerLine} />
        <span className={cls.dividerText}>{locale.dividerText}</span>
        <div className={cls.dividerLine} />
      </div>

      <div className={cls.ssoSection}>
        <button
          type="button"
          className={cls.outlineButton}
          onClick={() => {
            window.location.href = `${config.api_url}/api/connect/google`;
          }}>
          <RedesignImage src={`${publicIconPath}/Google.svg`} alt="" width={16} height={16} />
          <span className={cls.ssoButtonText}>{locale.ssoGoogle}</span>
        </button>
        <button
          type="button"
          className={cls.outlineButton}
          onClick={() => {
            window.location.href = `${config.api_url}/api/connect/facebook`;
          }}>
          <RedesignImage src={`${publicIconPath}/Facebook.svg`} alt="" width={16} height={16} />
          <span className={cls.ssoButtonText}>{locale.ssoFacebook}</span>
        </button>
        <button
          type="button"
          className={cls.outlineButton}
          onClick={() => {
            window.location.href = getAppleConnectUrl();
          }}>
          <RedesignImage src={`${publicIconPath}/Apple.svg`} alt="" width={16} height={16} />
          <span className={cls.ssoButtonText}>{locale.ssoApple}</span>
        </button>
      </div>

      <div className={cls.footerRow}>
        <span className={cls.footerText}>{locale.alreadyAccountText}</span>
        <Link className={cls.linkPrimary} to={loginLink}>
          {locale.loginText}
        </Link>
      </div>

      <button type="button" className={cls.outlineButton} onClick={() => setHelpOpen(true)}>
        <RedesignImage src={`${publicIconPath}/Info.svg`} alt="" width={16} height={16} />
        <span className={cls.outlineButtonTextPrimary}>Besoin d&apos;aide ?</span>
      </button>
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default SignupForm;
